import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SubscribeGate from "@/components/SubscribeGate";
import { Button } from "@/components/ui/button";
import { STAGE_META, STATUS_META, money2 } from "@/lib/format";
import { Loader2, Wallet, TrendingUp, Package, BadgeCheck, ArrowRight, Sprout, Lock, CreditCard } from "lucide-react";
import { startGoogleLogin } from "@/components/Navbar";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (loading || !user) return;
    if (user.subscription_status !== "active" && user.role !== "admin") return;
    api.get("/investments/me").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [user, loading]);

  if (loading) return <Center><Loader2 className="w-8 h-8 text-amber-400 animate-spin" /></Center>;
  if (!user) return (
    <Center>
      <Lock className="w-10 h-10 text-amber-400" />
      <p className="text-emerald-100/60 mt-3">Please sign in to view your dashboard.</p>
      <Button onClick={startGoogleLogin} className="mt-5 bg-amber-500 hover:bg-amber-600 text-[#0b120e] font-semibold rounded-full h-11 px-6">Sign in with Google</Button>
    </Center>
  );
  if (user.subscription_status !== "active" && user.role !== "admin") return <SubscribeGate />;
  if (!items) return <Center><Loader2 className="w-8 h-8 text-amber-400 animate-spin" /></Center>;

  const totalInvested = items.reduce((s, i) => s + i.investment.amount, 0);
  const totalDividends = items.reduce((s, i) => s + (i.investment.dividend_amount || 0), 0);
  const totalPayout = items.reduce((s, i) => s + (i.investment.payout_amount || 0), 0);

  const manageBilling = async () => {
    try {
      const res = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = res.data.portal_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Billing portal is unavailable right now");
    }
  };

  const metrics = [
    { icon: Wallet, label: "Total Capital Earmarked", value: money2(totalInvested), color: "text-amber-400" },
    { icon: Package, label: "Active Blocks", value: items.length, color: "text-emerald-400" },
    { icon: TrendingUp, label: "Dividends Earned", value: money2(totalDividends), color: "text-fuchsia-400" },
    { icon: BadgeCheck, label: "Projected Payout", value: money2(totalPayout || totalInvested), color: "text-sky-400" },
  ];

  return (
    <div className="bg-[#080C0A] min-h-[calc(100vh-4rem)]">
      <div className="max-w-7xl mx-auto px-5 py-12">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-widest font-mono-data text-amber-400/90">Portfolio</span>
              <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-mono-data px-2 py-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300" data-testid="subscription-badge">
                <BadgeCheck className="w-3 h-3" /> {user.role === "admin" ? "Admin" : "Subscribed"}
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mt-2">Welcome, {user.name?.split(" ")[0]}</h1>
          </div>
          <div className="flex items-center gap-2">
            {user.role !== "admin" && (
              <Button onClick={manageBilling} variant="outline" data-testid="manage-billing-btn" className="border-emerald-500/30 bg-transparent text-emerald-100 hover:bg-emerald-500/10 rounded-full h-11 px-5">
                <CreditCard className="w-4 h-4 mr-2" /> Manage Membership
              </Button>
            )}
            <Link to="/marketplace"><Button className="bg-amber-500 hover:bg-amber-600 text-[#0b120e] font-semibold rounded-full h-11 px-6" data-testid="dash-marketplace-btn">Browse Opportunities <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {metrics.map((m, i) => (
            <div key={i} className="bg-[#121A15] border border-emerald-900/40 rounded-2xl p-6" data-testid={`metric-${i}`}>
              <m.icon className={`w-6 h-6 ${m.color}`} />
              <div className="text-2xl font-bold text-white mt-3 font-mono-data">{m.value}</div>
              <div className="text-xs text-emerald-100/50 mt-1 uppercase tracking-wider">{m.label}</div>
            </div>
          ))}
        </div>

        <h2 className="text-xl font-semibold text-white mb-5">Your Investments</h2>
        {items.length === 0 ? (
          <div className="glass rounded-2xl p-16 text-center">
            <Sprout className="w-10 h-10 text-emerald-500/40 mx-auto" />
            <p className="text-emerald-100/50 mt-4">You haven't invested in any blocks yet.</p>
            <Link to="/marketplace"><Button className="mt-5 bg-amber-500 hover:bg-amber-600 text-[#0b120e] font-semibold rounded-full">Explore Opportunities</Button></Link>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map(({ investment, opportunity }) => {
              if (!opportunity) return null;
              const stage = STAGE_META[opportunity.logistics_stage] || STAGE_META["Growing Phase"];
              const status = STATUS_META[opportunity.status] || STATUS_META.open;
              return (
                <div key={investment.id} className="bg-[#121A15] border border-emerald-900/40 rounded-2xl p-5 flex flex-col md:flex-row md:items-center gap-5 hover:border-amber-500/30 transition-all" data-testid={`dash-investment-${investment.id}`}>
                  <img src={opportunity.image_url || "https://images.unsplash.com/photo-1634651423818-d8decca1934a?crop=entropy&cs=srgb&fm=jpg&q=85&w=400"} alt="" className="w-full md:w-28 h-28 md:h-20 rounded-xl object-cover" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-lg font-semibold text-white">{opportunity.crop}</h3>
                      <span className="text-xs text-emerald-100/50">• {opportunity.country}</span>
                      <span className={`text-[10px] uppercase tracking-wider font-mono-data px-2 py-0.5 rounded-full border ${stage.color}`}>{opportunity.logistics_stage}</span>
                      <span className={`text-[10px] uppercase tracking-wider font-mono-data px-2 py-0.5 rounded-full border ${status.color}`}>{status.label}</span>
                    </div>
                    <p className="text-sm text-emerald-100/50 mt-1">{opportunity.farm_company}</p>
                  </div>
                  <div className="flex gap-6 font-mono-data">
                    <div><div className="text-xs text-emerald-100/50">Contributed</div><div className="text-white font-semibold">{money2(investment.amount)}</div></div>
                    <div><div className="text-xs text-emerald-100/50">Dividend</div><div className={investment.dividend_paid ? "text-amber-400 font-semibold" : "text-emerald-100/40"}>{investment.dividend_paid ? money2(investment.dividend_amount) : "Pending"}</div></div>
                    <div><div className="text-xs text-emerald-100/50">Payout</div><div className={investment.dividend_paid ? "text-fuchsia-400 font-semibold" : "text-emerald-100/40"}>{investment.dividend_paid ? money2(investment.payout_amount) : "—"}</div></div>
                  </div>
                  <Link to={`/opportunity/${opportunity.id}`}><Button variant="ghost" size="icon" className="text-emerald-100/50 hover:text-white"><ArrowRight className="w-5 h-5" /></Button></Link>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const Center = ({ children }) => (
  <div className="min-h-[calc(100vh-4rem)] flex flex-col items-center justify-center text-center px-5 bg-[#080C0A]">{children}</div>
);
